import type { AxiosInstance } from 'axios';
import { getDeployStatus, setDeployStatus } from './deployStatus';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipDeployDetector?: boolean;
  }
}

function isDetectorSkipped(config: unknown): boolean {
  return (config as { skipDeployDetector?: boolean; } | null)?.skipDeployDetector === true;
}

const DEPLOY_STATUS_CODES = new Set([502, 503, 504]);
const FAILURE_THRESHOLD = 2;
const POLL_INTERVAL_MS = 3000;

const baseURL =
  (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) ||
  window.location.origin;

let consecutiveFailures = 0;
let pollHandle: ReturnType<typeof setInterval> | null = null;

function isDeployError(error: any): boolean {
  if (!error || typeof error !== 'object') return false;
  if (error.response && typeof error.response.status === 'number') {
    return DEPLOY_STATUS_CODES.has(error.response.status);
  }
  return true;
}

function stopPolling() {
  if (pollHandle !== null) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

function startPolling() {
  if (pollHandle !== null) return;
  pollHandle = setInterval(async () => {
    try {
      const res = await fetch(`${baseURL}/api/method/frappe.ping`, {
        method: 'GET',
        cache: 'no-store',
      });
      if (res.ok) {
        stopPolling();
        consecutiveFailures = 0;
        // Reload so panels re-fetch and any error popups shown during the
        // outage are torn down. Overlay stays visible until the new page
        // renders, which initializes isDeploying back to false.
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }
    } catch {
      // backend still unreachable — keep polling
    }
  }, POLL_INTERVAL_MS);
}

export function attachDeployDetector(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => {
      if (isDetectorSkipped(response.config)) {
        return response;
      }
      consecutiveFailures = 0;
      return response;
    },
    (error) => {
      if (isDetectorSkipped(error?.config)) {
        return Promise.reject(error);
      }
      if (isDeployError(error)) {
        consecutiveFailures += 1;
        if (consecutiveFailures >= FAILURE_THRESHOLD && !getDeployStatus()) {
          setDeployStatus(true);
          startPolling();
        }
      } else {
        consecutiveFailures = 0;
      }
      return Promise.reject(error);
    },
  );
}
